import { Box, Paper, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type SiestaStepCardProps = {
  steps: { title: string }[];
  currentStep: number;
  maxBehind?: number;
  dimmed: boolean;
  minimized: boolean;
};

export function SiestaStepCard({
  steps,
  currentStep,
  maxBehind = 2,
  dimmed = false,
  minimized = false,
}: SiestaStepCardProps) {
  const index = Math.max(0, Math.min(steps.length - 1, currentStep - 1));
  const current = steps[index];
  const visibleSteps = steps.slice(0, -1);
  const isHiddenLastStep = currentStep >= steps.length;

  if (isHiddenLastStep) {
    return null;
  }

  const behindCount = Math.min(maxBehind, Math.max(0, visibleSteps.length - currentStep));

  const baseCardStyle: React.CSSProperties = {
    borderRadius: 24,
    background: 'rgba(0,0,0,0.05)',
    backdropFilter: 'blur(50px)',
    border: '1px solid rgba(253,253,253,0.15)',
  };

  const backgroundByDepth: Record<number, string> = {
    1: 'rgba(119, 47, 23, 1)',
    2: 'rgba(102, 41, 21, 1)',
  };

  return (
    <Box w={374} pt={behindCount * 8} style={{ overflow: 'visible' }}>
      <Box pos="relative">
        {Array.from({ length: behindCount }).map((_, i) => {
          const depth = behindCount - i;

          const y = -depth * 10;

          const scale = 1 - depth * 0.1;

          return (
            <Paper
              key={`behind-${depth}`}
              radius={24}
              py={minimized ? 'xs' : 'sm'}
              px="md"
              pos="absolute"
              display="flex"
              bg={backgroundByDepth[depth] ?? backgroundByDepth[2]}
              inset={0}
              opacity={dimmed ? 0.3 : 1}
              style={{
                ...baseCardStyle,
                zIndex: 0,
                alignItems: 'center',
                justifyContent: 'center',
                transform: `translateY(${y}px) scale(${scale})`,
                transformOrigin: 'center',
                pointerEvents: 'none',
              }}
            />
          );
        })}

        <Paper
          radius={24}
          py={minimized ? 'xs' : 'sm'}
          px="md"
          pos="relative"
          display="flex"
          style={{
            ...baseCardStyle,
            zIndex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            ta="center"
            c="white"
            fw={FONT_WEIGHTS.bold}
            size={minimized ? 'md' : 'xl'}
            opacity={minimized ? 0.5 : 1}
            style={{ transition: 'all 300ms ease-in-out' }}
          >
            {current?.title ?? ''}
          </Text>
        </Paper>
      </Box>
    </Box>
  );
}
